import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppState } from 'src/app';
import { NewRelicLogger } from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { OperationTimeService } from 'src/core/services/operation-time.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
import { SharedSolaceStreamService } from 'src/solace/sharedSolace.stream.service';

declare var solace: any;
declare var SolHB: any; // tslint:disable-line

@Injectable()
export class SharedSolaceMiddlewareService implements OnDestroy {
  private solaceWrapper: any;
  private solaceProps: any;
  public isSolaceConnected = false;

  private storeSubscription: Subscription;
  private workingHoursSubscription: Subscription;

  private marketListenerSubscription: Subscription;
  private centralHourSubscription: Subscription;

  private solaceSubscription: Subscription;

  private startTime: any;
  private endTime: any;

  private isDisconnecting = false;
  private userHash = '';
  private user: string;

  private connectCalled = false;
  private operationTime: any = {
    isWorkingDay: false,
    isMarketOpen: false,
    isMarketClose: false,
    isMarketPpp: false,
  };

  constructor(
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private store: Store<AppState>,
    private operationTimeService: OperationTimeService,
    private newRelicLogger: NewRelicLogger,
  ) {}

  ngOnDestroy() {
    this.storeSubscription.unsubscribe();
    this.workingHoursSubscription.unsubscribe();
    this.marketListenerSubscription.unsubscribe();
    this.solaceSubscription.unsubscribe();
  }

  _detectMarketConditions() {
    this.marketListenerSubscription = this.store
      .select('loggedInUser')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => {
          this.user = value.user;
          this.userHash = value.hash;
          return value.hash === undefined;
        }),
      )
      .subscribe((value: any) => {
        this.disconnectSolace();
      });

    this.centralHourSubscription = this.store
      .select('params')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => {
          const currentTime: Date = new Date(value.centralHour);

          let control = false;

          if (currentTime === undefined || isNaN(currentTime.getTime())) {
            control = false;
          } else if (
            this.startTime === undefined ||
            this.endTime === undefined
          ) {
            control = false;
          } else if (
            currentTime.getHours() >= this.startTime.getHours() &&
            currentTime.getMinutes() >= this.startTime.getMinutes() &&
            currentTime.getHours() < this.endTime.getHours()
          ) {
            control = false;
          } else if (
            currentTime.getHours() === this.endTime.getHours() &&
            currentTime.getMinutes() <= this.endTime.getMinutes()
          ) {
            control = false;
          } else {
            control = true;
          }

          return control;
        }),
      )
      .subscribe((value: any) => {
        if (!value && this.isSolaceConnected) {
          this.disconnectSolace();
        }
      });
  }

  init(solaceConfigurations: any, solaceTopics: any[]): boolean {
    this._detectMarketConditions();

    this.solaceProps = solaceTopics;

    this.initSolace(solaceConfigurations, (messageCallback: any) =>
      this.sharedSolaceStreamService.processCallbackStream(messageCallback),
    );

    return true;
  }

  initSolace(configuration: any, messageCallback: any): void {
    const sessionProperties: any = new solace.SessionProperties();
    sessionProperties.url = configuration.url;
    sessionProperties.password = configuration.password;
    sessionProperties.vpnName = configuration.vpnName;
    sessionProperties.userName = configuration.userName;
    sessionProperties.transportProtocol = solace.TransportProtocol.WS_BINARY;
    sessionProperties.clientName = configuration.userId;

    this.solaceWrapper = new SolHB.ClientWrapper({
      solaceSessionProperties: sessionProperties,
      eventCallback: (data: any) => {
        this.eventSolaceCallback(data);
      },
      messageCallback,
      connectRetryCount: +configuration.connectRetryCount, // overriding a default property.
      subscriptionTimeout: +configuration.subscriptionTimeout,
      subscriptionRetryCount: +configuration.subscriptionRetryCount,
      subscriptionRetryInterval: +configuration.subscriptionRetryInterval,
    });
  }

  _connectSolace() {
    if (this.solaceWrapper) {
      this.solaceWrapper.connect();

      for (const prop of this.solaceProps) {
        this.subscribeTopic(prop);
      }

      this.solaceProps = { ...this.solaceProps, serviceToken: this.userHash };
      this.isSolaceConnected = true;
    }
  }

  trySolaceConnection() {
    const REDO_TIME = 200;

    if (this.isDisconnecting) {
      setTimeout(() => {
        this.trySolaceConnection();
      }, REDO_TIME);
    } else {
      if (!this.isSolaceConnected) {
        this.solaceSubscription = this.store
          .select('initializers')
          .pipe(
            map((value: any) => value.toJS()),
            filter(
              (value: any) => value.solace === true && !this.connectCalled,
            ),
          )
          .subscribe((value: any) => {
            this.connectCalled = true;
            this._connectSolace();
          });
      }
    }
  }

  eventSolaceCallback(data: any) {
    const SOLACE_SESSION_DOWN = 'SESSION_DOWN';
    if (data.indicationType === SOLACE_SESSION_DOWN) {
      console.warn('::::::: SESSION_DOWN ::::::');
      this.solaceWrapper.disconnect();
      this.isSolaceConnected = false;
      this.connectCalled = false;
      this.newRelicLogger.setEvent(SOLACE_SESSION_DOWN, '');
      this.connectSolace();
    }
  }

  isMarketOpen(startHours: any, endHours: any, currentHour: any): boolean {
    const OPEN_HOUR = startHours.getHours();
    const OPEN_MINUTES = startHours.getMinutes();
    const CLOSE_HOUR = endHours.getHours();
    const CLOSE_MINUTES = endHours.getMinutes();

    // console.log('isMarketOpen: OPEN_HOUR: %o  CLOSE_HOUR: %o  OPEN_MINUTES: %o', OPEN_HOUR, CLOSE_HOUR, OPEN_MINUTES);
    let isOpen = false;
    if (
      currentHour.getHours() >= OPEN_HOUR &&
      currentHour.getMinutes() >= OPEN_MINUTES &&
      currentHour.getHours() < CLOSE_HOUR
    ) {
      isOpen = true;
    } else if (
      currentHour.getHours() === CLOSE_HOUR &&
      currentHour.getMinutes() <= CLOSE_MINUTES
    ) {
      isOpen = true;
    } else {
      isOpen = false;
    }
    return isOpen;
  }

  isMarketClose(startHours: any, endHours: any, currentHour: any): boolean {
    const CLOSE_HOUR = endHours.getHours();
    const CLOSE_MINUTES = endHours.getMinutes();

    let isClose = false;
    if (currentHour.getHours() > CLOSE_HOUR) {
      isClose = true;
    } else if (currentHour.getHours() === CLOSE_HOUR) {
      if (currentHour.getMinutes() >= CLOSE_MINUTES) {
        isClose = true;
      }
    }

    return isClose;
  }

  isMarketPpp(startHours: any, endHours: any, currentHour: any): boolean {
    const CLOSE_HOUR = endHours.getHours();
    const CLOSE_PPP = 40;

    let isPpp = false;
    if (currentHour.getHours() === CLOSE_HOUR - 1) {
      if (currentHour.getMinutes() >= CLOSE_PPP) {
        isPpp = true;
      }
    }
    return isPpp;
  }

  validateWorkingHours(startHours: any, endHours: any) {
    const OPEN_HOUR = startHours.getHours();
    const OPEN_MINUTES = startHours.getMinutes();
    const CLOSE_HOUR = endHours.getHours();
    const CLOSE_MINUTES = endHours.getMinutes();

    this.storeSubscription = this.store
      .select('params')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => {
          const currentHour: any = new Date(value.centralHour);

          let control = false;

          if (currentHour === undefined || isNaN(currentHour.getTime())) {
            control = false;
          }

          this.operationTime.isMarketClose = this.isMarketClose(
            startHours,
            endHours,
            currentHour,
          );
          this.operationTime.isMarketOpen = this.isMarketOpen(
            startHours,
            endHours,
            currentHour,
          );
          this.operationTime.isMarketPpp = this.isMarketPpp(
            startHours,
            endHours,
            currentHour,
          );
          this.operationTimeService.changeOption(this.operationTime);

          if (
            currentHour.getHours() >= OPEN_HOUR &&
            currentHour.getMinutes() >= OPEN_MINUTES &&
            currentHour.getHours() < CLOSE_HOUR
          ) {
            control = true;
          } else if (
            currentHour.getHours() === CLOSE_HOUR &&
            currentHour.getMinutes() <= CLOSE_MINUTES
          ) {
            control = true;
          } else {
            this.disconnectSolace();
            control = false;
          }

          return control;
        }),
      )
      .subscribe((value: any) => {
        this.trySolaceConnection();
      });
  }

  connectSolace(): void {
    this.workingHoursSubscription = this.store
      .select('marketOperationTime')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => value !== undefined),
      )
      .subscribe((value: any) => {
        this.startTime = new Date(value.startTime);
        this.endTime = new Date(value.endTime);

        if (value.isWorkingDay) {
          this.operationTime.isWorkingDay = true;
          this.validateWorkingHours(this.startTime, this.endTime);
        } else {
          this.disconnectSolace();
        }
      });
  }

  disconnectSolace(): void {
    if (this.isDisconnecting) {
      return;
    }

    if (this.isSolaceConnected) {
      if (this.solaceWrapper) {
        this.setDisconnecting();
        /*
         * (2018-11-16)
         * Removed since solace's appliances kills subscriptions when a client has been disconnected
         */

        // for (const prop of this.solaceProps) {
        //   this.unSubscribeTopic(prop);
        //   prop.serviceToken = '';
        // }

        this.solaceWrapper.disconnect();
        this.solaceWrapper.dispose();
        this.solaceWrapper = undefined;

        this.isSolaceConnected = false;
        this.connectCalled = false;
      }
    }
  }

  setDisconnecting() {
    const WAIT_TIME = 3000;

    this.isDisconnecting = true;
    setTimeout(() => {
      this.isDisconnecting = false;
    }, WAIT_TIME);
  }

  subscribeTopic(topic: any) {
    if (this.solaceWrapper !== undefined) {
      topic = {
        ...topic,
        subscriptionTopic: new ChangeCharacterFromIssue().ChangeToAmpersand(
          topic.subscriptionTopic,
        ),
        correlationKey: {
          Symbol: new ChangeCharacterFromIssue().ChangeToAmpersand(
            topic.correlationKey.Symbol,
          ),
        },
      };
      this.solaceWrapper.subscribe(topic);
    }
  }

  unSubscribeTopic(topic: any) {
    if (this.solaceWrapper !== undefined) {
      topic = {
        ...topic,
        subscriptionTopic: new ChangeCharacterFromIssue().ChangeToAmpersand(
          topic.subscriptionTopic,
        ),
        correlationKey: {
          Symbol: new ChangeCharacterFromIssue().ChangeToAmpersand(
            topic.correlationKey.Symbol,
          ),
        },
      };
      this.solaceWrapper.unsubscribe(topic);
    }
  }
}
